import React, { useState } from "react";
import { Box, Tooltip, useTheme, Dialog, DialogContent, Typography } from "@mui/material";
import { AutoAwesome, DriveFileRenameOutline, VisibilityOutlined } from "@mui/icons-material";
import AiAuditAgreement from "./AiAuditAgreement";
import { useAuthSlice } from "../../../redux/slice/authSlice";
import { keyframes } from "@mui/system";
import Button from "../../common/Button";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { fetchAiServiceAgreementStatus, resetServiceAgreementStatus, useAiAuditReducer } from "../../../redux/slice/AI/AiAuditSlice";
import { fetchDocumentByIdCommonApi, resetDocumentData, setSingleAlertObj, SingleAlertInfo, useCommonReducer } from "../../../redux/slice/commonSlice";
import PDFViewer from "../../common/pdf/PDFViewer";

// Animation keyframes for gradient
const moveGradient = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

interface AiMedicalAuditInfoProps {
    size?: 'small' | 'medium' | 'large';
    className?: string;
    style?: React.CSSProperties;
    serviceName?: string;
    tooltipText?: string;
}

const AiMedicalAuditInfo: React.FC<AiMedicalAuditInfoProps> = ({ 
    size = 'medium',
    className,
    style,
    serviceName = "",
    tooltipText = "Run AI Medical Note Audit",
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const { userInfo } = useAuthSlice();
    const dispatch = useAppDispatch();
    const [showFeaturePopup, setShowFeaturePopup] = useState(false);
    const [showAgreement, setShowAgreement] = useState(false);
    const {serviceAgreementStatus} = useAiAuditReducer();
    const [showPDF, setShowPDF] = useState<{ isShow: boolean, fileName: string }>({ isShow: false, fileName: "" });
    const [pdfContent, setPDFContent] = useState<string>("");
    const { documentDataFromCommonApi } = useCommonReducer();

    const handleButtonClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(fetchAiServiceAgreementStatus(serviceName)).then((res)=>{
            if(res.meta && res.meta.requestStatus==="fulfilled"){
            setShowFeaturePopup(true);
            }
        }).catch((err)=>{
            console.log(err);
        });
    };

    const handleSignAgreement = () => {
        setShowFeaturePopup(false);
        setShowAgreement(true);
    };

    const handlePreviewSignAgreement = ()=>{
        if(serviceAgreementStatus && serviceAgreementStatus?.signedDocumentId){
             dispatch(fetchDocumentByIdCommonApi(serviceAgreementStatus?.signedDocumentId.toString())).then((res)=>{
                setPDFContent(res.payload.content);
                setShowPDF({ isShow: true, fileName: res.payload.documentName })
             })
        }else{
            let errorObj:SingleAlertInfo = {
                message: "No signed agreement found to preview.",
                alertType: "error",
            };
            return dispatch(setSingleAlertObj(errorObj));
        }
    }

    const handleClosePDFViewer = () => {
        setShowPDF({ isShow: false, fileName: "" });
        dispatch(resetDocumentData());
        // Also close the feature popup when PDF viewer is closed
        setShowFeaturePopup(false);
        dispatch(resetServiceAgreementStatus());
    };

    const handleAgreementSigned = () => {
        setShowAgreement(false);
    };

    const handleAgreementCancel = () => {
        setShowAgreement(false);
        // Close PDF viewer if it's open
        setShowPDF({ isShow: false, fileName: "" });
    };

    const handleCloseFeaturePopup = () => {
        setShowFeaturePopup(false);
        // Close PDF viewer if it's open
        setShowPDF({ isShow: false, fileName: "" });
        dispatch(resetServiceAgreementStatus());
    };

    const buttonStyles = {
        borderRadius: "24px",
        padding: "10px 24px",
        textTransform: "none" as const,
        fontSize: size === 'small' ? "0.875rem" : size === 'large' ? "1.125rem" : "1rem",
        fontWeight: 600,
        color: "#ffffff",
        
        // Eye-catching AI-themed gradient background
        background: isDark
            ? "linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7, #ec4899)"
            : "linear-gradient(135deg, #4f46e5, #7c3aed, #9333ea, #db2777)",
        backgroundSize: "300% 300%",
        animation: `${moveGradient} 4s ease infinite`,
        
        // Enhanced shadow for prominence
        // boxShadow: isDark 
        //     ? "0 4px 16px rgba(139, 92, 246, 0.5)" 
        //     : "0 4px 16px rgba(124, 58, 237, 0.4)",
        
        // transition: "transform 0.2s ease, box-shadow 0.2s ease",
        
        "&:hover": {
            // transform: "translateY(-2px)",
            // boxShadow: isDark 
            //     ? "0 6px 20px rgba(139, 92, 246, 0.6)" 
            //     : "0 6px 20px rgba(124, 58, 237, 0.5)",
            background: isDark
                ? "linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7, #ec4899)"
                : "linear-gradient(135deg, #4f46e5, #7c3aed, #9333ea, #db2777)",
        },
        
        "&:active": {
            transform: "translateY(0)",
        },
    };

    return (
        <>
            <Tooltip title={tooltipText} arrow placement="top">
                <Box
                    className={className}
                    sx={{
                        display: "inline-flex",
                        ...style
                    }}
                >
                    <Button
                        onClick={handleButtonClick}
                        sx={buttonStyles}
                    >
                        Medical Note Audit Agreement
                    </Button>
                </Box>
            </Tooltip>

            {/* Feature Description Popup */}
            <Dialog
                open={showFeaturePopup}
                onClose={handleCloseFeaturePopup}
                maxWidth="md"
                fullWidth
            >
                <DialogContent sx={{ p: 4 }}>
                    <Box>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold", mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
                            AI Medical Note Audit
                        </Typography>

                        <Typography variant="body1" paragraph>
                            Our AI-powered Medical Note Audit tool helps healthcare professionals ensure the completeness, accuracy, and compliance of medical documentation.
                        </Typography>
                        {(serviceAgreementStatus?.userSubscriptionStatus === null || serviceAgreementStatus?.userSubscriptionStatus !== "SIGNED") ? (<>
                        <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: "bold" }}>
                            Key Features:
                        </Typography>
                        <Typography variant="body1" component="div">
                            <ul>
                                <li>Automated analysis of medical notes for completeness</li>
                                <li>Identification of missing or incomplete information</li>
                                <li>Compliance checking against documentation standards</li>
                                <li>Suggestions for improving documentation quality</li>
                                <li>HIPAA-compliant processing and secure data handling</li>
                            </ul>
                        </Typography>
                        </>):(<>
                         <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: "bold" }}>
                            How It Works:
                        </Typography>
                        <Typography variant="body1" component="div">
                            <ol>
                                <li>Upload your medical notes in PDF format</li>
                                <li>Our AI analyzes the document for key elements</li>
                                <li>Receive detailed audit results and recommendations</li>
                                <li>Review and implement suggested improvements</li>
                            </ol>
                        </Typography>
                        </>)}

                        {(serviceAgreementStatus?.userSubscriptionStatus === null || serviceAgreementStatus?.userSubscriptionStatus !== "SIGNED")  ? (
                            <Box sx={{ mt: 4, p: 2, bgcolor: isDark ? "rgba(139, 92, 246, 0.1)" : "rgba(124, 58, 237, 0.1)", borderRadius: 2 }}>
                                <Typography variant="body2" paragraph sx={{ mb: 2 }}>
                                    To use this feature, you must first review and sign the AI Audit Agreement which outlines the terms, limitations, and your responsibilities when using this tool.
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<DriveFileRenameOutline />}
                                    onClick={handleSignAgreement}
                                    sx={{
                                        background: isDark
                                            ? "linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7)"
                                            : "linear-gradient(135deg, #4f46e5, #7c3aed, #9333ea)",
                                        "&:hover": {
                                            background: isDark
                                                ? "linear-gradient(135deg, #5558e3, #7c3aed, #9333ea)"
                                                : "linear-gradient(135deg, #4338ca, #6d28d9, #7e22ce)",
                                        }
                                    }}
                                >
                                    Sign Agreement
                                </Button>
                            </Box>
                        ):(<>
                        <Box sx={{ mt: 4, p: 2, bgcolor: isDark ? "rgb(20, 106, 19)" : "rgba(40, 129, 44, 0.63)", borderRadius: 2 }}>
                                <Typography variant="body2" paragraph sx={{ mb: 2 }}>
                                    ✓ You have signed the AI Audit Agreement. You can now use this feature through the individual file audit buttons.
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<VisibilityOutlined />}
                                    onClick={handlePreviewSignAgreement}
                                        sx={{
                                            background: isDark
                                                ? "linear-gradient(135deg, #22c55e, #16a34a, #15803d)"
                                                : "linear-gradient(135deg, #4ade80, #22c55e, #16a34a)",
                                            "&:hover": {
                                                background: isDark
                                                    ? "linear-gradient(135deg, #1fb455, #15803d, #166534)"
                                                    : "linear-gradient(135deg, #34d399, #16a34a, #15803d)",
                                            }
                                        }}

                                >
                                   Signed Medical Note Audit Agreement
                                </Button>
                            </Box>
                        {/* <Box sx={{ mt: 4, p: 2, bgcolor: "green", borderRadius: 2 }}>
                                <Typography variant="body2">
                                    ✓ You have signed the AI Audit Agreement. You can now use this feature through the individual file audit buttons.
                                </Typography>
                            </Box> */}
                        </>)}

                        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                            <Button onClick={handleCloseFeaturePopup} color="error">Close</Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* AI Audit Agreement Dialog */}
            <AiAuditAgreement
                open={showAgreement}
                onAgreementSigned={handleAgreementSigned}
                onCancel={handleAgreementCancel}
                userServiceAgreementStatus={serviceAgreementStatus}
            />
            {showPDF.isShow &&
                <PDFViewer
                    isOpen={showPDF.isShow}
                    onClose={handleClosePDFViewer}
                    title={showPDF.fileName}
                    fileName={showPDF.fileName}
                    base64String={documentDataFromCommonApi?.content ? documentDataFromCommonApi?.content : pdfContent}
                />
            }
        </>
    );
};

export default AiMedicalAuditInfo;
